import {Action, getModule, Module} from 'vuex-module-decorators';
import store from '@/store';
import {PModuleMeasurement, DtcpTahoModule} from '@/views/system/modules/interfaces';
import {PModuleType} from '@/enums/configs';
import {PMProcessActionType, PMProcessSystemType} from '@/views/system/modules/enums';
import {httpClient} from '@/api/baseApi';
import {EntityModule} from '../../entityModule';
import {PModuleStoreModule} from '../interfaces';
import {DateTimeInterval} from '@/utils/dateTimeItervalUtils';
import {getEventData, getEventTypes, getLastEvents, getSignal} from '../base';
import {PMActionType} from '@/views/system/modules/enums';
import {isNumber} from '@/utils/commonUtils';

/*
    Динамический модуль хранилища vuex для работы с ПМ датчика тахометра
*/

const moduleName = 'ptahoStoreModule';

function transformGet(elem: any): DtcpTahoModule {
  return {
    id: elem.id,
    guid: elem.guid,
    name: elem.name,
    type: PModuleType.DtcpTahoModule,
    description: elem.description,
    nodes: elem.nodes,
    alias: elem.alias,
    device: elem.device,
    stat_channel: elem.stat_channel,
    raw_signal_channel: elem.raw_signal_channel,
    mult: elem.mult,
    meas_count: elem.meas_count,
    meas_period_ms: elem.meas_period_ms,
    channels_enabled: elem.channels_enabled,
    parent_pm: elem.parent_pm ?? null,
    measurements: [],
  };
}

function getMeasurements(elem: any): PModuleMeasurement[] {
  return elem.measurements.map((elem: any) => ({
    measurementId: elem.module_type_measurement_id,
    measurementDescription: elem.module_type_measurement_description,
    topWarningBoundary: elem.top_warning_boundary,
    topDangerBoundary: elem.top_danger_boundary,
    topErrorBoundary: elem.top_error_boundary,
    bottomWarningBoundary: elem.bottom_warning_boundary,
    bottomDangerBoundary: elem.bottom_danger_boundary,
    bottomErrorBoundary: elem.bottom_error_boundary,
  }));
}

function transformGetById(elem: any): DtcpTahoModule {
  const model = transformGet(elem);
  model.measurements = getMeasurements(elem);
  return model;
}

function transformCreateEdit(elem: any): DtcpTahoModule {
  const measurements: PModuleMeasurement[] = getMeasurements(elem);

  return {
    name: elem.name,
    alias: elem.alias,
    guid: elem.guid,
    type: PModuleType.DtcpTahoModule,
    description: elem.description,
    nodes: elem.nodes,
    device: elem.device,
    stat_channel: elem.stat_channel,
    raw_signal_channel: elem.raw_signal_channel,
    mult: elem.mult,
    meas_count: elem.meas_count,
    meas_period_ms: elem.meas_period_ms,
    channels_enabled: elem.channels_enabled,
    parent_pm: elem.parent_pm ?? null,
    measurements: measurements,
  };
}

function transformSave(model: DtcpTahoModule): any {
  const measurements: any[] = model.measurements.map((elem: any) => {
    const m: any = {
      module_type_measurement_id: elem.measurementId,
      module_type_measurement_description: elem.measurementDescription,
    };
    if (isNumber(elem.topWarningBoundary)) m.top_warning_boundary = elem.topWarningBoundary;
    if (isNumber(elem.topDangerBoundary)) m.top_danger_boundary = elem.topDangerBoundary;
    if (isNumber(elem.topErrorBoundary)) m.top_error_boundary = elem.topErrorBoundary;
    if (isNumber(elem.bottomWarningBoundary))
      m.bottom_warning_boundary = elem.bottomWarningBoundary;
    if (isNumber(elem.bottomDangerBoundary)) m.bottom_danger_boundary = elem.bottomDangerBoundary;
    if (isNumber(elem.bottomErrorBoundary)) m.bottom_error_boundary = elem.bottomErrorBoundary;
    return m;
  });

  return {
    name: model.name,
    description: model.description,
    nodes: model.nodes,
    device: model.device,
    stat_channel: model.stat_channel,
    raw_signal_channel: model.raw_signal_channel,
    mult: model.mult,
    meas_count: model.meas_count,
    meas_period_ms: model.meas_period_ms,
    channels_enabled: model.channels_enabled,
    parent_pm: model.parent_pm,
    measurements: measurements,
  };
}

@Module({
  name: moduleName,
  store: store,
  dynamic: true,
  namespaced: true,
})
export class DtcpTahoStoreModule extends EntityModule implements PModuleStoreModule {
  protected routeName = 'dtcp-tachometer-modules';

  get all(): DtcpTahoModule[] {
    return super.all as DtcpTahoModule[];
  }

  //Получение списка объектов с сервера
  @Action
  public async fetchAll() {
    return super.fetchAll(transformGet);
  }

  //Получение модели для создания объекта
  @Action
  public async create(): Promise<{data: DtcpTahoModule}> {
    return super.create(transformCreateEdit) as Promise<{data: DtcpTahoModule}>;
  }

  //Получение модели для редактирования объекта
  @Action
  public async edit(id: any): Promise<{data: DtcpTahoModule}> {
    return super.edit(id, transformCreateEdit) as Promise<{data: DtcpTahoModule}>;
  }

  //Создание/реактирование объекта
  @Action
  public async save(model: DtcpTahoModule) {
    return super.save(model, transformSave);
  }

  //Получение объекта по идентификатору
  @Action
  async getById(id: any): Promise<{data: DtcpTahoModule}> {
    return super.getById(id, transformGetById) as Promise<{data: DtcpTahoModule}>;
  }

  /* TODO: Данные методы не вынести в базовый класс (т. к. в store наследование больше 2хмодулей не работает) */

  //Получение сигнала
  @Action
  async getSignal(params: {
    id: number;
    dateInterval: DateTimeInterval;
    fieldAlias: string;
  }): Promise<any> {
    try {
      return await getSignal(this.routeName, params);
    } catch (e) {
      return Promise.reject(e);
    }
  }

  //Выполнить действие
  @Action
  async processAction(params: {id: number; action: PMProcessActionType}) {
    const {data} = await httpClient.put(`/${this.routeName}/${params.id}/processes/`, {
      action: params.action,
    });
    return data;
  }

  //Выполнить системную команду программного модуля
  @Action
  async processSystem(params: {id: number; action: PMProcessSystemType}) {
    const {data} = await httpClient.put(`/${this.routeName}/${params.id}/processes/system`, {
      action: params.action,
    });
    return data;
  }

  //Получение типов событий
  @Action
  async getEventTypes(): Promise<any> {
    try {
      return await getEventTypes(this.routeName);
    } catch (e) {
      return Promise.reject(e);
    }
  }

  //Получение списка последних событий
  @Action
  async getLastEvents(params: {id: number; eventType: string; limit?: number}): Promise<any> {
    try {
      return await getLastEvents(this.routeName, params);
    } catch (e) {
      return Promise.reject(e);
    }
  }

  //Получение данных события
  @Action
  async getEventData(params: {id: number; eventType: string; timestamp: number}): Promise<any> {
    try {
      return await getEventData(this.routeName, params);
    } catch (e) {
      return Promise.reject(e);
    }
  }

  //Выполнить действие start_record
  @Action
  async startRecordAction(params: {id: number; duration: number}): Promise<any> {
    try {
      const {data} = await httpClient.put(
        `/${this.routeName}/${params.id}/actions/${PMActionType.StartRecord}`,
        {
          duration: params.duration,
        },
      );
      return data;
    } catch (e) {
      return Promise.reject(e);
    }
  }
}

export default getModule(DtcpTahoStoreModule);
